// usePanZoom.ts — zoom and pan for an SVG graph.
//
// The hook returns a transform for a <g> wrapping the drawing plus the
// handlers that drive it:
//   - drag anywhere on the SVG pans;
//   - ctrl/cmd + wheel (and a trackpad pinch, which browsers report as a
//     ctrl-wheel) zooms about the pointer — a plain wheel still scrolls the
//     page, so the graph never traps scrolling;
//   - zoomIn / zoomOut / reset back the card's buttons.
//
// A drag that moves more than a few pixels swallows the click that ends it,
// so panning never toggles the commit under the pointer.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;
const STEP = 1.25;
const DRAG_SLOP_PX = 4;

export interface View {
  k: number; // scale
  x: number; // translate, in viewBox units
  y: number;
}

const IDENTITY: View = { k: 1, x: 0, y: 0 };

function clampK(k: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k));
}

// Zoom to k2 keeping the viewBox point (px, py) fixed on screen.
export function zoomAbout(v: View, k2: number, px: number, py: number): View {
  const k = clampK(k2);
  const r = k / v.k;
  return { k, x: px - (px - v.x) * r, y: py - (py - v.y) * r };
}

// `svg` is the element itself (from a callback ref held in state), not a
// ref object: the graph's SVG mounts only after its data loads, and the
// wheel listener has to attach when it appears.
export function usePanZoom(svg: SVGSVGElement | null, center: { x: number; y: number }) {
  const [view, setView] = useState<View>(IDENTITY);
  const drag = useRef<{ cx: number; cy: number; start: View; moved: boolean } | null>(null);
  const suppressClick = useRef(false);

  // Client pixels -> viewBox units, from the SVG's current screen matrix.
  const toSvg = useCallback((clientX: number, clientY: number) => {
    const m = svg?.getScreenCTM();
    if (!svg || !m) return { x: clientX, y: clientY, scale: 1 };
    const p = new DOMPoint(clientX, clientY).matrixTransform(m.inverse());
    return { x: p.x, y: p.y, scale: m.a || 1 };
  }, [svg]);

  // The wheel listener is attached by hand: React's onWheel is passive, and
  // a passive listener cannot stop the page from scrolling while zooming.
  useEffect(() => {
    if (!svg) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const p = toSvg(e.clientX, e.clientY);
      const factor = Math.exp(-e.deltaY * 0.0025);
      setView((v) => zoomAbout(v, v.k * factor, p.x, p.y));
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [svg, toSvg]);

  const onPointerDown = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    drag.current = { cx: e.clientX, cy: e.clientY, start: view, moved: false };
    const move = (ev: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = ev.clientX - d.cx;
      const dy = ev.clientY - d.cy;
      if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP_PX) return;
      d.moved = true;
      const scale = toSvg(ev.clientX, ev.clientY).scale;
      setView({ ...d.start, x: d.start.x + dx / scale, y: d.start.y + dy / scale });
    };
    const up = () => {
      if (drag.current?.moved) suppressClick.current = true;
      drag.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [view, toSvg]);

  // Capture phase, so the row's own onClick never sees a pan's click.
  const onClickCapture = useCallback((e: ReactMouseEvent) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const zoomIn = useCallback(() => setView((v) => zoomAbout(v, v.k * STEP, center.x, center.y)), [center.x, center.y]);
  const zoomOut = useCallback(() => setView((v) => zoomAbout(v, v.k / STEP, center.x, center.y)), [center.x, center.y]);
  const reset = useCallback(() => setView(IDENTITY), []);

  return {
    view,
    transform: `translate(${view.x} ${view.y}) scale(${view.k})`,
    dragging: drag.current?.moved ?? false,
    handlers: { onPointerDown, onClickCapture },
    zoomIn,
    zoomOut,
    reset,
  };
}
